// shared/src/cron-schedule.ts
// PARAMETER-DRIVEN, TZ-AWARE cron schedule (2026-06-05 rewrite).
//
// Supersedes the hardcoded UTC-cron `SCHEDULE` const + DST-pair model. The
// schedule now lives in the `CRON_SCHEDULE` runtime_param (D1, edit on the
// Operations Params tab — no code change, no deploy) as a list of high-level
// RECURRENCE RULES. Real IANA-tz wall-clock math (util/tz-clock.ts) computes
// fire times, so DST is handled automatically — no more two-entries-per-run
// DST pairs and no ±5-min `dstAnchor` guard.
//
// Design: docs/cron-schedule-parameterization-2026-06-05-brief-revision.md
//
// The cron-dispatcher still fires every UTC minute (forced by the Free-plan
// cron-trigger cap, D-48, AND it runs the stuck-brief reaper + runs sweeper on
// the same tick). Each tick it loads CRON_SCHEDULE, runs matchSchedule() for
// the current minute, and fans out matched rules via Service Binding RPC.

import { z } from 'zod';
import { wallClockInTz, utcMsForWallClock, isValidTimeZone } from './util/tz-clock.js';

export type ScheduleTarget =
  | 'REFRESH' | 'CHAIN' | 'AMM' | 'CLOSE' | 'SCORER' | 'MACRO_SCORER' | 'BACKUP' | 'ACCOUNT' | 'EXPORT' | 'ORACLE' | 'PURGE';

export const SCHEDULE_TARGETS = [
  'REFRESH', 'CHAIN', 'AMM', 'CLOSE', 'SCORER', 'MACRO_SCORER', 'BACKUP', 'ACCOUNT', 'EXPORT', 'ORACLE', 'PURGE',
] as const;

// S-53: the RPC method names each target actually exposes — the SINGLE source of
// truth mirrored by the cron-dispatcher dispatch switch (scheduled.ts case block).
// `method` was a bare z.string().min(1), so a typo ('runFires') or a cross-target
// method (target REFRESH + method 'runClose') passed the Params-tab write and only
// threw at fire time (`unknown <TARGET> method`), silently skipping that run. The
// CronScheduleParamSchema superRefine below rejects an invalid (target, method)
// pair at WRITE time. Keep this in lockstep with the dispatcher switch: a new RPC
// method must be added here AND there in the same commit.
export const TARGET_METHODS: Record<ScheduleTarget, readonly [string, ...string[]]> = {
  REFRESH:      ['runFire', 'scoreSignals'],
  CHAIN:        ['runChainFire'],
  // PURGE hosts the nightly D1 retention purge. It dispatches to the SAME
  // chain-workflow RPC (env.CHAIN.runRetentionPurge) but is a DISTINCT
  // ScheduleTarget so the brief's per-target `next_fires.CHAIN` countdown reflects
  // only actual brief fires, not the 03:00 UTC maintenance job (which had made the
  // brief header count down to the purge). Kept in lockstep with the
  // cron-dispatcher PURGE switch (scheduled.ts case 'PURGE').
  PURGE:        ['runRetentionPurge'],
  AMM:          ['runAmmFire'],
  // #55: runCloseHeal is the once-daily settled-close backfill sweep (distinct from the live
  // 16:00/18:30-ET runClose fires). Added here AND to the cron-dispatcher CLOSE switch in lockstep.
  CLOSE:        ['runClose', 'runCloseHeal'],
  SCORER:       ['runScore'],
  MACRO_SCORER: ['runMacroScore'],
  BACKUP:       ['runBackup', 'runWeeklyPromotion'],
  ACCOUNT:      ['runAccountFire'],
  EXPORT:       ['runDailyExport', 'runFullBackfill'],
  ORACLE:       ['runOracleFire', 'runOracleMacroFire', 'runOracleEquityFire'],
};

export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
export type DayName = typeof DAY_NAMES[number];

// ─── Zod schema for the CRON_SCHEDULE param ─────────────────────────────────

const TargetSchema = z.enum(SCHEDULE_TARGETS);
const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be HH:MM (24-hour)');

const DaySpecSchema = z.union([
  z.enum(['daily', 'weekdays', 'weekends']),
  z.array(z.enum(DAY_NAMES)).min(1),
]);
export type DaySpec = z.infer<typeof DaySpecSchema>;

// Interval rule — 24/7 cadence (REFRESH, AMM, ACCOUNT, hourly scoreSignals).
// Fires when (minutesSinceUTCmidnight % everyMinutes) === (atMinuteOffset ?? 0).
const IntervalRuleSchema = z.object({
  target:         TargetSchema,
  method:         z.string().min(1),
  everyMinutes:   z.number().int().positive(),
  atMinuteOffset: z.number().int().nonnegative().optional(),
  note:           z.string().optional(),
}).strict();

// Time-of-day rule — wall-clock time in an IANA tz (DST handled by Intl).
// Fires when wallClock(now, tz) === at AND weekday(now, tz) ∈ days.
const TimeOfDayRuleSchema = z.object({
  target:       TargetSchema,
  method:       z.string().min(1),
  at:           HHMM,
  tz:           z.string().min(1),
  days:         DaySpecSchema,
  // Catch-up window (default 0 = exact-minute, identical reliability to the old
  // cron match). >0: also fire if the tick lands within (due, due+grace] AND the
  // runs table shows no fire yet for this target/method in the window (dedup is
  // the dispatcher's job, keeping this matcher pure). Default 0 on CHAIN — a
  // double brief is worse (cost + false-done) than a rare missed run.
  graceMinutes: z.number().int().nonnegative().max(60).optional(),
  note:         z.string().optional(),
}).strict();

export const RecurrenceRuleSchema = z.union([IntervalRuleSchema, TimeOfDayRuleSchema]);
export type IntervalRule  = z.infer<typeof IntervalRuleSchema>;
export type TimeOfDayRule = z.infer<typeof TimeOfDayRuleSchema>;
export type RecurrenceRule = z.infer<typeof RecurrenceRuleSchema>;

function isTimeOfDay(r: RecurrenceRule): r is TimeOfDayRule {
  return 'at' in r;
}

/** Parse a validated 'HH:MM' string. The HHMM Zod regex guarantees both parts. */
function parseHHMM(at: string): { h: number; m: number } {
  const parts = at.split(':');
  return { h: parseInt(parts[0]!, 10), m: parseInt(parts[1]!, 10) };
}

// The CRON_SCHEDULE param: a non-empty list of rules. Refinements enforce that
// (a) every time-of-day rule names a valid IANA tz, and (b) the infra-critical
// BACKUP + REFRESH targets each have ≥1 rule so a bad Params edit can never
// silently disable backups or ingestion. Validated on WRITE (admin-params PATCH
// parses the full merged ParamsSchema), so D1 never holds an unfireable schedule.
export const CronScheduleParamSchema = z.array(RecurrenceRuleSchema).min(1).superRefine((rules, ctx) => {
  rules.forEach((r, i) => {
    if (isTimeOfDay(r) && !isValidTimeZone(r.tz)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [i, 'tz'], message: `invalid IANA time zone: ${r.tz}` });
    }
    // S-53: reject a method the target doesn't expose (typo / cross-target) at
    // WRITE time, not at fire time. TARGET_METHODS mirrors the dispatcher switch.
    if (!TARGET_METHODS[r.target].includes(r.method)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [i, 'method'],
        message: `unknown method '${r.method}' for target ${r.target} (valid: ${TARGET_METHODS[r.target].join(', ')})`,
      });
    }
  });
  const targets = new Set(rules.map((r) => r.target));
  for (const required of ['BACKUP', 'REFRESH'] as const) {
    if (!targets.has(required)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `schedule must include at least one ${required} rule (infra safety rail)` });
    }
  }
});
export type CronScheduleParam = z.infer<typeof CronScheduleParamSchema>;

// ─── Day resolution ─────────────────────────────────────────────────────────

const WEEKDAY_SET = new Set([1, 2, 3, 4, 5]);      // Mon..Fri
const WEEKEND_SET = new Set([0, 6]);               // Sun, Sat

function dayMatches(days: DaySpec, weekday: number): boolean {
  if (days === 'daily')    return true;
  if (days === 'weekdays') return WEEKDAY_SET.has(weekday);
  if (days === 'weekends') return WEEKEND_SET.has(weekday);
  // explicit name list
  return days.some((name) => DAY_NAMES.indexOf(name) === weekday);
}

// ─── Exact-minute fire predicate (shared by matcher + projections) ──────────

/** True if `rule` fires at the exact minute of `at` (seconds/ms ignored). */
export function ruleFiresExactlyAt(rule: RecurrenceRule, at: Date): boolean {
  if (isTimeOfDay(rule)) {
    const wc = wallClockInTz(at.getTime(), rule.tz);
    if (!dayMatches(rule.days, wc.weekday)) return false;
    const { h, m } = parseHHMM(rule.at);
    return wc.hour === h && wc.minute === m;
  }
  // interval rule — minutes since UTC midnight
  const minsSinceUTCMidnight = at.getUTCHours() * 60 + at.getUTCMinutes();
  const offset = (rule.atMinuteOffset ?? 0) % rule.everyMinutes;
  return minsSinceUTCMidnight % rule.everyMinutes === offset;
}

// ─── matchSchedule — what fires at `now` ────────────────────────────────────

export interface ScheduleMatch {
  rule:   RecurrenceRule;
  /** 'exact' = the rule's fire-minute is `now`. 'grace' = a catch-up candidate
   *  (time-of-day rule with graceMinutes>0 whose due minute passed within the
   *  window); the dispatcher must dedup grace matches against the runs table
   *  before dispatching, so a recovered tick never double-fires. */
  reason: 'exact' | 'grace';
}

/** Pure matcher: returns the rules due to fire at the minute containing `now`.
 *  `now` is truncated to the minute for time-of-day comparison. */
export function matchSchedule(rules: readonly RecurrenceRule[], now: Date): ScheduleMatch[] {
  const out: ScheduleMatch[] = [];
  for (const rule of rules) {
    if (ruleFiresExactlyAt(rule, now)) {
      out.push({ rule, reason: 'exact' });
      continue;
    }
    // Grace / catch-up — time-of-day rules only, opt-in via graceMinutes.
    if (isTimeOfDay(rule) && (rule.graceMinutes ?? 0) > 0) {
      const wc = wallClockInTz(now.getTime(), rule.tz);
      if (!dayMatches(rule.days, wc.weekday)) continue;
      const { h, m } = parseHHMM(rule.at);
      const dueMin = h * 60 + m;
      if (wc.minutes > dueMin && wc.minutes <= dueMin + (rule.graceMinutes ?? 0)) {
        out.push({ rule, reason: 'grace' });
      }
    }
  }
  return out;
}

// ─── Next / last fire projections (api-worker /api/ops/schedule + countdowns) ─
// SCHEDULE is small and these run server-side in api-worker, not on the
// dispatcher hot path. They MUST still be cheap: the prior impl scanned every
// minute in the window and called the Intl-backed wallClockInTz() per minute, so
// buildScheduleResponse issued ~77k Intl.formatToParts per request (intervalMs's
// 8-day window walked twice per time-of-day rule) → Free-plan worker CPU limit →
// `exceededCpu` 503 on the deployed dashboard. Time-of-day rules now iterate by
// DAY (≤ ~window/1440 + 2 iterations) and compute each day's fire instant
// directly via utcMsForWallClock — O(days) Intl calls, byte-identical results to
// the old minute-scan. Interval rules keep the (Intl-free) modular minute-walk.
// See feedback_intl_dateformat_cpu_trap.

const DAY_MS = 86_400_000;

function truncToMinute(d: Date): Date {
  const t = new Date(d.getTime());
  t.setUTCSeconds(0, 0);
  return t;
}

/** UTC ms of noon on the local calendar day `dir` days from `probeMs`'s local
 *  date in `tz`. Noon never falls in the 02:00–03:00 DST transition window, so
 *  the anchor is unambiguous — used only to step the date cursor day by day. */
function adjacentLocalNoon(probeMs: number, tz: string, dir: 1 | -1): number {
  const wc = wallClockInTz(probeMs, tz);
  const [y, mo, da] = wc.date.split('-').map((s) => parseInt(s, 10)) as [number, number, number];
  const shifted = new Date(Date.UTC(y, mo - 1, da, 12, 0) + dir * DAY_MS);
  const ms = utcMsForWallClock(
    shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate(), 12, 0, tz,
  );
  // Noon always exists (utcMsForWallClock only returns null inside the spring
  // gap), but stay total: fall back to the naive UTC-shifted instant.
  return ms ?? (Date.UTC(y, mo - 1, da, 12, 0) + dir * DAY_MS);
}

/** Smallest fire instant strictly AFTER `from`, within `withinMinutes`. */
export function nextFireOfRule(rule: RecurrenceRule, from: Date, withinMinutes = 1440): Date | null {
  const fromMs = truncToMinute(from).getTime();
  if (!isTimeOfDay(rule)) {
    // Interval rule — modular minute-walk (no Intl, cheap).
    for (let i = 1; i <= withinMinutes; i++) {
      const cand = new Date(fromMs + i * 60_000);
      if (ruleFiresExactlyAt(rule, cand)) return cand;
    }
    return null;
  }
  const { h, m } = parseHHMM(rule.at);
  const endMs = fromMs + withinMinutes * 60_000;
  const maxDays = Math.ceil(withinMinutes / 1440) + 2;
  let probe = fromMs;
  for (let i = 0; i <= maxDays; i++) {
    const wc = wallClockInTz(probe, rule.tz);
    if (dayMatches(rule.days, wc.weekday)) {
      const [y, mo, da] = wc.date.split('-').map((s) => parseInt(s, 10)) as [number, number, number];
      const fireMs = utcMsForWallClock(y, mo, da, h, m, rule.tz);
      if (fireMs !== null && fireMs > fromMs && fireMs <= endMs) return new Date(fireMs);
    }
    probe = adjacentLocalNoon(probe, rule.tz, 1);
    if (probe > endMs + DAY_MS) break;
  }
  return null;
}

/** Most-recent fire instant strictly BEFORE `from`, within `withinMinutes`. */
export function lastFireOfRule(rule: RecurrenceRule, from: Date, withinMinutes = 1440): Date | null {
  const fromMs = truncToMinute(from).getTime();
  if (!isTimeOfDay(rule)) {
    for (let i = 1; i <= withinMinutes; i++) {
      const cand = new Date(fromMs - i * 60_000);
      if (ruleFiresExactlyAt(rule, cand)) return cand;
    }
    return null;
  }
  const { h, m } = parseHHMM(rule.at);
  const endMs = fromMs - withinMinutes * 60_000;
  const maxDays = Math.ceil(withinMinutes / 1440) + 2;
  let probe = fromMs;
  for (let i = 0; i <= maxDays; i++) {
    const wc = wallClockInTz(probe, rule.tz);
    if (dayMatches(rule.days, wc.weekday)) {
      const [y, mo, da] = wc.date.split('-').map((s) => parseInt(s, 10)) as [number, number, number];
      const fireMs = utcMsForWallClock(y, mo, da, h, m, rule.tz);
      if (fireMs !== null && fireMs < fromMs && fireMs >= endMs) return new Date(fireMs);
    }
    probe = adjacentLocalNoon(probe, rule.tz, -1);
    if (probe < endMs - DAY_MS) break;
  }
  return null;
}

/** Natural cadence of a rule in ms (interval → everyMinutes; time-of-day →
 *  gap between its next two fires). Null when un-estimable within the window.
 *  Used by the dashboard's overdue check (server-computed; the client no longer
 *  parses cron). */
export function intervalMsOfRule(rule: RecurrenceRule, from: Date, withinMinutes = 1440 * 8): number | null {
  if (!isTimeOfDay(rule)) return rule.everyMinutes * 60_000;
  const first = nextFireOfRule(rule, from, withinMinutes);
  if (first === null) return null;
  const second = nextFireOfRule(rule, first, withinMinutes);
  if (second === null) return null;
  return second.getTime() - first.getTime();
}

// ─── Human-readable label (wire shape: ScheduleEntryView.schedule) ──────────

function daySpecLabel(days: DaySpec): string {
  if (typeof days === 'string') return days;        // 'daily' | 'weekdays' | 'weekends'
  return days.join(',');
}

function tzLabel(tz: string): string {
  if (tz === 'America/New_York') return 'ET';
  return tz;
}

/** e.g. 'every 15 min', 'every 3h (:35)', 'weekends 12:00 ET', 'daily 00:30 UTC'. */
export function ruleLabel(rule: RecurrenceRule): string {
  if (!isTimeOfDay(rule)) {
    const base = rule.everyMinutes % 60 === 0
      ? `every ${rule.everyMinutes / 60}h`
      : `every ${rule.everyMinutes} min`;
    const off = rule.atMinuteOffset ? ` (:${String(rule.atMinuteOffset).padStart(2, '0')})` : '';
    return base + off;
  }
  return `${daySpecLabel(rule.days)} ${rule.at} ${tzLabel(rule.tz)}`;
}

// ─── Per-target / per-entry aggregates (parallel to the rules array) ────────

function emptyTargetMap(): Record<ScheduleTarget, Date | null> {
  return {
    REFRESH: null, CHAIN: null, AMM: null, CLOSE: null,
    SCORER: null, MACRO_SCORER: null, BACKUP: null, ACCOUNT: null, EXPORT: null, ORACLE: null, PURGE: null,
  };
}

export function nextFiresByTarget(
  rules: readonly RecurrenceRule[],
  from: Date,
  withinMinutes = 1440,
): Record<ScheduleTarget, Date | null> {
  const out = emptyTargetMap();
  for (const rule of rules) {
    const next = nextFireOfRule(rule, from, withinMinutes);
    if (next === null) continue;
    const prev = out[rule.target];
    if (prev === null || next.getTime() < prev.getTime()) out[rule.target] = next;
  }
  return out;
}

export function lastFiresByTarget(
  rules: readonly RecurrenceRule[],
  from: Date,
  withinMinutes = 1440,
): Record<ScheduleTarget, Date | null> {
  const out = emptyTargetMap();
  for (const rule of rules) {
    const last = lastFireOfRule(rule, from, withinMinutes);
    if (last === null) continue;
    const prev = out[rule.target];
    if (prev === null || last.getTime() > prev.getTime()) out[rule.target] = last;
  }
  return out;
}

export function nextFiresPerEntry(
  rules: readonly RecurrenceRule[],
  from: Date,
  withinMinutes = 1440,
): (Date | null)[] {
  return rules.map((r) => nextFireOfRule(r, from, withinMinutes));
}

export function lastFiresPerEntry(
  rules: readonly RecurrenceRule[],
  from: Date,
  withinMinutes = 1440,
): (Date | null)[] {
  return rules.map((r) => lastFireOfRule(r, from, withinMinutes));
}
